"use client"

import { motion } from 'framer-motion'
import Link from 'next/link'
import { ArrowLeft, FileText, Scale, User, Shield, Lock, AlertCircle, Settings } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Footer } from '@/components/Footer'
import { contactInfo } from "@/data/contact"

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-[#030818] text-white">
      {/* Background Effects */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 opacity-[0.03] grid-pattern-subtle" />
      </div>
   
       
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-slate-900 via-slate-950 to-black text-white py-20 border-b border-slate-800/50">
        <div className="container mx-auto px-6 relative z-10">
          <div className="flex items-center mb-8">
            <Link href="/">
              <Button
                variant="ghost"
                className="text-slate-300 hover:text-white hover:bg-white/5 border border-slate-700/50 hover:border-slate-600 backdrop-blur-sm transition-all duration-300"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>

           <motion.div
             initial={{ opacity: 0, y: 30 }}
             animate={{ opacity: 1, y: 0 }}
             transition={{ duration: 0.8 }}
             className="text-center max-w-3xl mx-auto px-4"
           >
             <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-[#00dd98]/10 border border-[#00dd98]/25 rounded-2xl mb-4 sm:mb-6 shadow-lg shadow-[#00dd98]/10">
               <FileText className="h-8 w-8 sm:h-10 sm:w-10 text-[#00dd98]" />
             </div>
             <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6">
               Terms of{" "}
               <span className="bg-gradient-to-r from-[#00dd98] via-[#17b88c] to-[#00dd98] bg-clip-text text-transparent">
                 Service
               </span>
             </h1>
             <div className="w-16 sm:w-24 h-1 bg-[#00dd98] mx-auto mb-4 sm:mb-6 rounded-full shadow-lg shadow-[#00dd98]/30" />
             <p className="text-lg sm:text-xl text-slate-300 leading-relaxed mb-3 sm:mb-4">
               Please read these terms carefully before using our services.
             </p>
             <p className="text-sm sm:text-base text-slate-400">
               <strong>Last Updated:</strong> October 27, 2025
             </p>
           </motion.div>
        </div>
      </div>
      
       <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-16 max-w-4xl relative z-10">
        
        <SectionCard 
          icon={<FileText className="h-6 w-6 text-[#00dd98]" />}
          iconBg="bg-[#00dd98]/10 border border-[#00dd98]/20"
          title="Introduction"
          content={
            <div>
              <p className="text-slate-300 mb-4 leading-relaxed">
                Welcome to ORAPEX. These Terms of Service (&ldquo;Terms&rdquo;) govern your use of our website orapx.com 
                and our services. By accessing or using our website or services, you agree to be bound by these Terms.
              </p>
              <p className="text-slate-300 leading-relaxed">
                If you do not agree to these Terms, please do not use our website or services. We reserve the 
                right to modify these Terms at any time, and your continued use of our services constitutes 
                acceptance of any changes.
              </p>
            </div>
          }
        />

        <SectionCard 
          icon={<Settings className="h-6 w-6 text-[#00dd98]" />}
          iconBg="bg-[#00dd98]/10 border border-[#00dd98]/20"
          title="Our Services"
          content={
            <div>
              <p className="text-slate-300 mb-3 leading-relaxed">
                ORAPEX provides technology solutions and services including:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-300">
                <li>AI-driven business solutions and automation</li>
                <li>Oracle APEX development and customization</li>
                <li>Oracle EBS extension and modernization</li>
                <li>Full-stack web and mobile application development</li>
                <li>Professional training programs and certification</li>
                <li>Consulting and technical support services</li>
              </ul>
              <p className="text-slate-300 mt-4 leading-relaxed">
                We reserve the right to modify, suspend, or discontinue any part of our services at any time 
                without prior notice.
              </p>
            </div>
          }
        />

        <SectionCard 
          icon={<User className="h-6 w-6 text-[#00dd98]" />}
          iconBg="bg-[#00dd98]/10 border border-[#00dd98]/20"
          title="Website Usage"
          content={
            <div>
              <p className="text-slate-300 mb-3 leading-relaxed">
                By using our website, you agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-300 mb-4">
                <li>Provide accurate information when contacting us</li>
                <li>Use our website for lawful purposes only</li>
                <li>Respect our intellectual property rights</li>
                <li>Not attempt to disrupt or damage our website</li>
              </ul>
              <p className="text-slate-300 leading-relaxed">
                We reserve the right to refuse service or remove content at our sole discretion.
              </p>
            </div>
          }
        />

        <SectionCard 
          icon={<Shield className="h-6 w-6 text-[#00dd98]" />}
          iconBg="bg-[#00dd98]/10 border border-[#00dd98]/20"
          title="Intellectual Property"
          content={
            <p className="text-slate-300 leading-relaxed">
              All content on our website, including text, graphics, logos, and images, is owned by ORAPEX and 
              protected by copyright laws. You may not reproduce or distribute our content without written permission.
            </p>
          }
        />

        <SectionCard 
          icon={<Lock className="h-6 w-6 text-[#00dd98]" />}
          iconBg="bg-[#00dd98]/10 border border-[#00dd98]/20"
          title="Privacy"
          content={
            <p className="text-slate-300 leading-relaxed">
              Your privacy is important to us. Our collection and use of personal information is governed 
              by our Privacy Policy. By using our website, you consent to our privacy practices.
            </p>
          }
        />

        <SectionCard 
          icon={<AlertCircle className="h-6 w-6 text-[#00dd98]" />}
          iconBg="bg-[#00dd98]/10 border border-[#00dd98]/20"
          title="Limitation of Liability"
          content={
            <p className="text-slate-300 leading-relaxed">
              Our services are provided &ldquo;as is&rdquo; without warranties. We strive to provide reliable services 
              but cannot guarantee uninterrupted access. We are not liable for any indirect or consequential damages.
            </p>
          }
        />

        <SectionCard 
          icon={<Scale className="h-6 w-6 text-[#00dd98]" />}
          iconBg="bg-[#00dd98]/10 border border-[#00dd98]/20"
          title="Governing Law"
          content={
            <p className="text-slate-300 leading-relaxed">
              These Terms are governed by the laws of the United States. Any disputes will be resolved through 
              appropriate legal channels.
            </p>
          }
        />

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="relative overflow-hidden bg-slate-900/30 rounded-xl sm:rounded-2xl shadow-lg shadow-black/20 p-4 sm:p-6 lg:p-8 text-white mb-6 sm:mb-8 border border-slate-800/50 backdrop-blur-sm"
        >
          {/* soft glow */}
          <div className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[#00dd98]/10 blur-3xl" />
          {/* accent line */}
          <div className="pointer-events-none absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-[#00dd98]/80 via-[#00dd98]/30 to-transparent" />

          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#00dd98]/10 border border-[#00dd98]/25 flex items-center justify-center text-[#00dd98] shrink-0">
              <User className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <h2 className="text-xl sm:text-2xl font-bold mb-2">Contact Information</h2>
              <p className="text-slate-300 leading-relaxed text-sm sm:text-base">
                If you have any questions about these Terms of Service, please contact us:
              </p>
            </div>
          </div>

          <div className="mt-5 bg-white/5 rounded-xl p-4 sm:p-6 border border-white/10">
            <p className="mb-2 text-sm sm:text-base">
              <strong>Email:</strong> {contactInfo.email}
            </p>
            <p className="mb-2 text-sm sm:text-base">
              <strong>Website:</strong> www.orapex.com
            </p>
            <p className="text-sm sm:text-base">
              <strong>Address:</strong> ORAPEX Solutions<br />
              United States, Saudi Arabia, United Arab Emirates, Jordan, Oman
            </p>
          </div>
        </motion.div>

       
      </div>

      <Footer />
    </div>
  )
}

interface SectionCardProps {
  icon: React.ReactNode
  iconBg: string
  title: string
  content: React.ReactNode
}

function SectionCard({ icon, iconBg, title, content }: SectionCardProps) {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="bg-slate-900/30 rounded-xl sm:rounded-2xl shadow-lg shadow-black/20 p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8 border border-slate-800/50 backdrop-blur-sm hover:bg-slate-900/40 transition-colors duration-300"
    >
      <div className="flex items-start space-x-3 sm:space-x-4 mb-4 sm:mb-6">
        <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 ${iconBg} rounded-xl flex items-center justify-center`}>
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-3 sm:mb-4">{title}</h2>
          <div className="text-sm sm:text-base">{content}</div>
        </div>
      </div>
    </motion.div>
  )
}
